import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type Ref,
} from "react";
import clsx from "clsx";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { DEFAULT_PLANUNG_COLOR, deleteAblage, listAblage } from "@/lib/montageplanung";
import type { PlanungAblageItem } from "@/lib/montageplanung";
import type { ConfirmDialogOptions } from "./useConfirmDialog";
import { MP_DRAG_THRESHOLD } from "./layoutConstants";
import { AblageFormDialog } from "./AblageFormDialog";
import { splitOrderLinkLabel } from "./displayLabels";

function ablageMeta(item: PlanungAblageItem): string | null {
  const parts: string[] = [];
  if (item.is_fixed) parts.push("Fix");
  if (item.is_blocker) parts.push("Blocker");
  const orders = item.orders ?? [];
  if (orders.length === 1) {
    parts.push(splitOrderLinkLabel(orders[0]!.order_id, orders[0]!.order_label).number);
  } else if (orders.length > 1) {
    parts.push(`${orders.length} Aufträge`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export function AblagePanel({
  canEdit,
  refreshToken,
  draggingId,
  hint,
  busy,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
  onConfirm,
  dropRef,
  parkHover,
}: {
  canEdit: boolean;
  refreshToken: number;
  draggingId: number | null;
  hint: string | null;
  busy: boolean;
  onDragStart: (item: PlanungAblageItem) => void;
  onDragMove: (item: PlanungAblageItem, clientX: number, clientY: number) => void;
  onDragEnd: (item: PlanungAblageItem, clientX: number, clientY: number) => void;
  onDragCancel: () => void;
  onConfirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  dropRef?: Ref<HTMLElement>;
  parkHover?: boolean;
}) {
  const [items, setItems] = useState<PlanungAblageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanungAblageItem | null>(null);

  const onDragStartRef = useRef(onDragStart);
  const onDragMoveRef = useRef(onDragMove);
  const onDragEndRef = useRef(onDragEnd);
  const onDragCancelRef = useRef(onDragCancel);
  onDragStartRef.current = onDragStart;
  onDragMoveRef.current = onDragMove;
  onDragEndRef.current = onDragEnd;
  onDragCancelRef.current = onDragCancel;

  const reload = useCallback(() => {
    setLoading(true);
    return listAblage()
      .then((data) => {
        setItems(data);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Ablage konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, refreshToken]);

  function openCreate() {
    setEditingItem(null);
    setFormOpen(true);
    setError(null);
  }

  function openEdit(item: PlanungAblageItem) {
    setEditingItem(item);
    setFormOpen(true);
    setError(null);
  }

  async function removeItem(item: PlanungAblageItem) {
    const ok = await onConfirm({
      title: "Montage aus der Ablage entfernen",
      message: `„${item.title}“ aus der Ablage löschen?`,
      confirmLabel: "Entfernen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteAblage(item.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Montage konnte nicht entfernt werden.");
    }
  }

  function beginDrag(event: ReactPointerEvent, item: PlanungAblageItem) {
    if (!canEdit || busy || event.button !== 0) return;
    const target = event.target;
    if (target instanceof Element && target.closest("button, input, textarea, a")) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    let started = false;

    const finish = (ev: PointerEvent, cancelled: boolean) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      if (!started) return;
      if (cancelled) onDragCancelRef.current();
      else onDragEndRef.current(item, ev.clientX, ev.clientY);
    };
    const move = (ev: PointerEvent) => {
      if (!started) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < MP_DRAG_THRESHOLD) return;
        started = true;
        onDragStartRef.current(item);
      }
      onDragMoveRef.current(item, ev.clientX, ev.clientY);
    };
    const up = (ev: PointerEvent) => finish(ev, false);
    const cancel = (ev: PointerEvent) => finish(ev, true);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  }

  return (
    <aside
      ref={dropRef}
      className={clsx("mp-ablage", parkHover && "is-park-hover")}
      aria-label="Ablage"
    >
      <div className="mp-ablage-head">
        <div className="min-w-0">
          <h2 className="mp-ablage-title">Ablage</h2>
          <p className="mp-ablage-subtitle">Bereit zum Einplanen</p>
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={openCreate}
            leftIcon={<PlusIcon className="h-4 w-4" />}
          >
            Neu
          </Button>
        )}
      </div>

      {hint && <p className="mp-ablage-hint">{hint}</p>}
      {error && <p className="mp-ablage-error">{error}</p>}

      <div className="mp-ablage-scroll">
        {loading && items.length === 0 ? (
          <div className="mp-ablage-empty">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="mp-ablage-empty">
            Noch keine Montage. Hier landen Aufträge, für die alles da ist und die eingeplant
            werden können.
          </p>
        ) : (
          <table className="mp-ablage-table">
            <thead>
              <tr>
                <th>Montage</th>
                <th>Pers.</th>
                <th>Tage</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const meta = ablageMeta(item);
                return (
                <tr
                  key={item.id}
                  className={clsx(
                    "mp-ablage-row",
                    canEdit && "is-draggable",
                    draggingId === item.id && "is-dragging",
                  )}
                  onPointerDown={(event) => beginDrag(event, item)}
                >
                  <td>
                    <span className="mp-ablage-name">
                      <span
                        className="mp-ablage-dot"
                        style={{ backgroundColor: item.color || DEFAULT_PLANUNG_COLOR }}
                        aria-hidden
                      />
                      <span className="mp-ablage-name-text">
                        <span className="mp-ablage-name-title" title={item.note || item.title}>
                          {item.title}
                        </span>
                        {meta ? <span className="mp-ablage-meta">{meta}</span> : null}
                      </span>
                    </span>
                  </td>
                  <td className="mp-ablage-num">{item.person_count}</td>
                  <td className="mp-ablage-num">{item.day_count}</td>
                  <td className="mp-ablage-actions">
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="mp-ablage-icon"
                          aria-label={`${item.title} bearbeiten`}
                          onClick={() => openEdit(item)}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="mp-ablage-icon"
                          aria-label={`${item.title} entfernen`}
                          onClick={() => void removeItem(item)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {canEdit && (
        <AblageFormDialog
          open={formOpen}
          item={editingItem}
          onClose={() => {
            setFormOpen(false);
            setEditingItem(null);
          }}
          onSaved={() => {
            void reload();
          }}
        />
      )}
    </aside>
  );
}
